<template>
  <div>
    <div class="container mini">
      <div class="bg-gray rounded-md shadow my-6">
        <div class="flex justify-between items-start">
          <div class="p-6 md:px-9 md:pt-9 pb-6">
            <Logo class="w-48 md:w-auto" />
          </div>
          <LangSwitcher />
        </div>
        <div class="pb-9 px-6 md:px-9 border-b border-purple-gray">
          <p class="leading-snug">
            {{ $t('auth.text') }}
          </p>
        </div>
        <div class="pb-5 px-6 md:px-9">
          <h2 class="font-semibold text-black-display mt-6 mb-3 text-2xl text-center ornament">
            {{ $t('auth.heading') }}
          </h2>
          <form class="pt-6 mb-4">
            <div
              class="mb-4"
              :class="{'input-error': (errors.email || errors.login) && !loading}"
            >
              <label
                class="block text-black-display text-base font-semibold mb-1"
                for="email"
              >
                {{ $t('auth.user') }}
              </label>
              <input
                id="email"
                v-model="email"
                class="appearance-none border border-gray-500 rounded z-10 relative text-xl w-full py-3 px-3 text-black-display mb-2 leading-tight focus:outline-none"
                type="text"
                @keyup.enter="triggerLogin"
              >
              <transition name="slide-up">
                <p
                  v-if="errors.email && !loading"
                  class="text-error z-1"
                >
                  {{ $t('auth.error.email') }}
                </p>
              </transition>
            </div>
            <div
              class="mb-7"
              :class="{'input-error': (errors.password || errors.login) && !loading}"
            >
              <label
                class="block text-black-display text-base font-semibold mb-1"
                for="password"
              >
                {{ $t('auth.password') }}
              </label>
              <input
                id="password"
                v-model="password"
                class="appearance-none border border-gray-500 rounded z-10 relative text-xl w-full py-3 px-3 text-black-display mb-2 leading-tight focus:outline-none"
                type="password"
                @keyup.enter="triggerLogin"
              >
              <transition name="slide-up">
                <p
                  v-if="errors.login && !loading"
                  key="loginErr"
                  class="text-error z-1"
                >
                  {{ $t('auth.error.login') }}
                </p>
                <p
                  v-if="errors.password && !loading"
                  key="passwErr"
                  class="text-error z-1"
                >
                  {{ $t('auth.error.password') }}
                </p>
              </transition>
            </div>
            <div class="flex items-center justify-center mb-9">
              <button
                :class="{'loading': loading}"
                class="btn h-14 px-9 flex items-center"
                type="button"
                @click="triggerLogin"
              >
                {{ $t('auth.login') }}
                <transition name="zoom-in">
                  <div
                    v-if="loading"
                    class="spinner w-5 h-5 ml-3 -mr-3 animate-spin"
                  >
                    <SpinnerIcon />
                  </div>
                </transition>
              </button>
            </div>
            <div class="justify-center">
              <p>
                <span>{{ $t('auth.domainRegister') }},</span>
                <router-link
                  :to="{name: 'DOMAIN_REGISTER'}"
                  class="font-bold text-primary"
                >
                  {{ $t('auth.clickHere') }}
                </router-link>
              </p>
              <p>
                <span>{{ $t('auth.forgotPassword') }},</span>
                <router-link
                  :to="{name: 'FORGOT_PASSWORD'}"
                  class="font-bold text-primary"
                >
                  {{ $t('auth.clickHere') }}
                </router-link>
              </p>
              <p>{{ $t('auth.contactAdministrator') }}</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import LangSwitcher from '@/components/LangSwitcher';
import Logo from '@/assets/img/logo.svg';
import SpinnerIcon from '@/assets/icons/spinner.svg';
import { START, REGISTER_CONFIRMATION } from '@/router/routes.names';

export default {
  name: 'Login',
  components: {
    LangSwitcher,
    Logo,
    SpinnerIcon,
  },
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      responded: false,
      blocked: false,
      errors: {
        email: false,
        login: false,
        password: false,
      },
    };
  },
  watch: {
    email() {
      this.errors.email = false;
      this.errors.login = false;
    },
    password() {
      this.errors.password = false;
      this.errors.login = false;
    },
  },
  methods: {
    ...mapActions('auth', ['login']),
    triggerLogin() {
      if (!this.blocked) {
        this.blockRequestsFor(700);
        this.responded = false;

        const { email, password } = this;

        this.login({ email, password })
          .then(() => {
            this.loading = false;
            this.$router.push({ name: START });
          })
          .catch((e) => {
            this.responded = true;
            if (this.blocked === false) {
              this.loading = false;
            }
            if (e.response) {
              const response = e.response.data;
              if (response.errors) {
                this.errors = {
                  ...response.errors,
                };
              } else if (response.exceptionMessage) {
                if (response.exceptionMessage === 'User not verified') {
                  this.$router.push({ name: REGISTER_CONFIRMATION });
                } else {
                  this.errors.login = true;
                }
              }
            }
          });
      }
    },
    blockRequestsFor(blockDuration) {
      this.blocked = true;
      this.loading = true;
      setTimeout(() => {
        this.blocked = false;
        if (this.responded === true) {
          this.loading = false;
        }
      }, blockDuration);
    },
  },
};
</script>
<style lang="scss" scoped>
  .spinner {
    position: absolute;
    right: 36px;
    top: 18px;
  }
  input {
    transition: all .5s ease;
  }
  button {
    position: relative;
    transition: all .5s ease;
    padding-right: 36px;
    &.loading {
      padding-right: 58px;
    }
  }
</style>
